import { useEffect, useRef, useState } from "react";
import { Button } from "@/design-system/Button";

import { isUtcInstant, validatePeriod, type Preset, type SelectedPeriod } from "./period";

const PRESETS: readonly { readonly id: Preset; readonly label: string }[] = [
  { id: "today", label: "Сегодня" },
  { id: "yesterday", label: "Вчера" },
  { id: "7d", label: "7д" },
  { id: "30d", label: "30д" },
  { id: "90d", label: "90д" },
  { id: "180d", label: "6м" },
  { id: "1y", label: "1г" },
  { id: "all", label: "Всё время" },
];

type Draft = { readonly since: string; readonly until: string };

function draftOf(period: SelectedPeriod): Draft {
  return { since: period.since ?? "", until: period.until ?? "" };
}

/**
 * The resolved span, short enough to sit under the control.
 *
 * One date when the span is inside a day, two when it crosses one. The exact
 * instants stay reachable in the title — a reader who needs microseconds is
 * reconciling something, and a reader who does not should not be paying for
 * them with a line of screen.
 */
function compactSpan(since: string, until: string): string {
  const from = new Date(since);
  const to = new Date(until);
  const day = (at: Date): string =>
    at.toLocaleDateString("ru-RU", { day: "numeric", month: "short", timeZone: "UTC" });
  const time = (at: Date): string =>
    at.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });

  return day(from) === day(to)
    ? `${day(from)} · ${time(from)}–${time(to)} UTC`
    : `${day(from)} ${time(from)} — ${day(to)} ${time(to)} UTC`;
}

export function PeriodControl({
  period,
  effectiveSince = null,
  effectiveUntil = null,
  validationError = null,
  onPeriod,
}: {
  readonly period: SelectedPeriod;
  readonly effectiveSince?: string | null;
  readonly effectiveUntil?: string | null;
  readonly validationError?: string | null;
  readonly onPeriod: (period: SelectedPeriod) => void;
}): React.JSX.Element {
  const [customOpen, setCustomOpen] = useState(period.preset === "custom");
  const [draft, setDraft] = useState<Draft>(() => draftOf(period));
  const [error, setError] = useState<string | null>(null);
  const sinceRef = useRef<HTMLInputElement>(null);
  const untilRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCustomOpen(period.preset === "custom");
    setDraft(draftOf(period));
    setError(validationError);
  }, [period.preset, period.since, period.until, validationError]);

  function select(preset: Preset): void {
    setError(null);
    setCustomOpen(false);
    onPeriod({ since: null, until: null, preset });
  }

  function openCustom(): void {
    setCustomOpen(true);
    setError(null);
  }

  function apply(): void {
    const next = { since: draft.since || null, until: draft.until || null, preset: "custom" as const };
    const validation = validatePeriod(next);
    if (validation.kind !== "exact") {
      setError(validation.kind === "invalid" ? validation.message : "Укажите корректные границы периода в UTC.");
      (next.since === null || !isUtcInstant(next.since) ? sinceRef : untilRef).current?.focus();
      return;
    }
    setError(null);
    onPeriod(next);
  }

  return (
    <section className="flex min-w-0 flex-col gap-2" aria-label="Период истории">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
        <div
          role="group"
        aria-label="Предустановки периода истории"
        className="inline-flex w-fit max-w-full flex-wrap gap-0.5 rounded-row bg-background-base p-0.5"
        data-testid="period-control"
      >
        {PRESETS.map(({ id, label }) => (
          <Button
            key={id}
            variant="ghost"
            size="sm"
            selected={period.preset === id}
            aria-pressed={period.preset === id}
            onClick={() => {
              select(id);
            }}
            className="h-8 px-3 text-label-caps pointer-coarse:h-11"
          >
            {label}
          </Button>
        ))}
        <Button
          variant="ghost"
          size="sm"
          selected={period.preset === "custom"}
          aria-pressed={period.preset === "custom"}
          aria-expanded={customOpen}
          onClick={openCustom}
          className="h-8 px-3 text-label-caps pointer-coarse:h-11"
        >
          Период
        </Button>
        </div>

        {effectiveSince !== null && effectiveUntil !== null && (
          // On the control's own row rather than a line of its own: the two
          // are one statement — this period, resolved to these instants — and
          // splitting them cost a row of the page to say it twice.
          <p
            className="text-helper-sm tabular-nums text-text-muted"
            data-testid="period-effective-span"
            title={`Точный интервал UTC: ${effectiveSince} — ${effectiveUntil}`}
          >
            {compactSpan(effectiveSince, effectiveUntil)}
          </p>
        )}
      </div>

      {customOpen && (
        <div className="flex flex-wrap items-end gap-2 border-l-2 border-border-strong pl-3">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-helper-sm text-text-secondary">
            Начало периода (UTC)
            <input
              ref={sinceRef}
              value={draft.since}
              onChange={(event) => {
                setDraft({ ...draft, since: event.target.value });
              }}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={error === null ? undefined : "period-error"}
              placeholder="2026-08-01T00:00:00Z"
              className="min-h-11 min-w-0 rounded-row border border-border-subtle bg-surface-default px-3 font-mono text-body-main text-text-primary placeholder:text-text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            />
          </label>
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-helper-sm text-text-secondary">
            Конец периода (UTC)
            <input
              ref={untilRef}
              value={draft.until}
              onChange={(event) => {
                setDraft({ ...draft, until: event.target.value });
              }}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={error === null ? undefined : "period-error"}
              placeholder="2026-08-24T12:00:00Z"
              className="min-h-11 min-w-0 rounded-row border border-border-subtle bg-surface-default px-3 font-mono text-body-main text-text-primary placeholder:text-text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            />
          </label>
          <Button
            variant="secondary"
            onClick={apply}
            className="min-h-11 text-label-caps"
          >
            Применить
          </Button>
          {error !== null && (
            <p id="period-error" role="alert" className="basis-full text-helper-sm text-status-negative">
              {error}
            </p>
          )}
        </div>
      )}

    </section>
  );
}
